import { Link, useParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { fetchContent, fill, text } from '../api/content'
import { attachmentReviewUrl } from '../api/attachments'
import {
  fetchReviewTranscript,
  NotHereError,
  reviewPath,
  reviewTranscriptKey,
  type Message,
  type ReviewSource,
} from '../api/conversations'
import { MessageRow } from './ConversationView'

/**
 * A conversation read from outside it (D-626, D-629, D-631): the studio over
 * an adult's account, staff or parent, or a guardian over her dancer's login.
 *
 * **Read-only, and its own screen rather than the participant's.** The
 * participant's screen marks the conversation read on open, which the server
 * refuses for somebody with no seat, and it offers a composer, reactions,
 * edits and the mute - none of which a reader here may use. What is shared is
 * the bubble, so a fix to how a message looks lands in one place (A-17).
 *
 * **Projected as the reviewed account sees it**: her messages on the right,
 * the mention marker where she was named. The line under the title says whose
 * side it is read from, and the line above the transcript says that this open
 * has been recorded - both sentences are the studio's (A-16).
 *
 * **A failed load is never an empty transcript** (D-209).
 */
export default function SupervisoryTranscript({ source }: { source: ReviewSource }) {
  const {
    staffId = '',
    guardianId = '',
    dancerId = '',
    id = '',
  } = useParams<{ staffId?: string; guardianId?: string; dancerId?: string; id: string }>()

  const personId = source === 'staff' ? staffId : source === 'guardians' ? guardianId : dancerId

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const transcript = useInfiniteQuery({
    queryKey: reviewTranscriptKey(source, personId, id),
    queryFn: ({ pageParam }) => fetchReviewTranscript(source, personId, id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.page.hasMore && last.page.messages.length > 0 ? last.page.messages[0].sentAt : undefined,
    enabled: personId !== '' && id !== '',
  })

  const pages = transcript.data?.pages ?? []
  const header = pages[0]?.header
  const messages: Message[] = [...pages].reverse().flatMap((p) => p.page.messages)
  const now = new Date()

  const back = `${reviewPath(source, personId)}/conversations`

  return (
    <div className="page-shell px-5 py-8">
      <div className="flex flex-col gap-3">
        <Link to={back} className="inline-flex min-h-[44px] items-center gap-2 text-[13px] font-semibold text-ink-2">
          <ArrowLeft size={16} aria-hidden="true" />
          {text(content, 'messages.review.back', 'Back to the list')}
        </Link>

        {transcript.isError ? (
          <div className="rounded-2xl border border-line bg-surface px-5 py-6" role="alert">
            <p className="text-[13px] leading-relaxed text-ink-2">
              {transcript.error instanceof NotHereError
                ? text(content, 'messages.notHere', 'That conversation is not here.')
                : text(content, 'messages.review.failed', 'Those conversations could not be loaded. Try again in a moment.')}
            </p>
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <h1 className="font-display text-2xl wrap-anywhere">
                {header?.title ?? text(content, 'messages.loading', 'Loading…')}
              </h1>
              {header && (
                <p className="text-[12px] text-ink-2">
                  {fill(content, 'messages.review.theirs', { name: header.subjectName }) ||
                    `Read from ${header.subjectName}'s side`}
                  {header.kind === 'Group' && (
                    <>
                      {' · '}
                      {fill(content, 'messages.group.people', { count: header.people.length + 1 }) ||
                        `${header.people.length + 1} people`}
                      {' · '}
                      {header.replyMode === 'Private'
                        ? text(content, 'messages.group.private', 'Replies go to the sender only')
                        : text(content, 'messages.group.shared', 'Replies go to everybody')}
                    </>
                  )}
                </p>
              )}
            </div>

            <p className="text-[12px] font-semibold text-ink-2" role="note">
              {text(content, 'messages.review.readOnly', 'Read-only. This open has been recorded.')}
            </p>

            <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface px-4 py-4">
              {transcript.isPending && (
                <p className="text-[13px] text-ink-2" role="status">
                  {text(content, 'messages.loading', 'Loading…')}
                </p>
              )}

              {transcript.hasNextPage && (
                <button
                  type="button"
                  onClick={() => void transcript.fetchNextPage()}
                  disabled={transcript.isFetchingNextPage}
                  className="self-center rounded-full border border-line px-3 py-1 text-[12px] font-semibold text-ink-2"
                >
                  {text(content, 'messages.older', 'Show earlier messages')}
                </button>
              )}

              {transcript.isSuccess && messages.length === 0 && (
                <p className="text-[13px] text-ink-2">{text(content, 'messages.nothingYet', 'Nothing said yet.')}</p>
              )}

              <ol className="flex flex-col gap-3">
                {messages.map((m) => (
                  <MessageRow
                    key={m.id}
                    conversationId={id}
                    message={m}
                    content={content}
                    now={now}
                    offered={header?.reactionsOffered ?? []}
                    mayReview={false}
                    readOnly
                    pictureUrl={attachmentReviewUrl}
                  />
                ))}
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
